import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Divider,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { AppContext } from "../../../context/AppContext";

const headerRowSx = { backgroundColor: "#1565C0" };
const headerCellSx = { color: "#fff", fontWeight: 500 };

const asMoney = (v) => {
  const s = String(v ?? "").replace(",", ".").trim();
  if (s === "" || s === ".") return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const isDigits = (s) => /^\d+$/.test(String(s || ""));

const normalizeMoneyInput = (v) => {
  let s = String(v ?? "").replace(/,/g, ".");
  s = s.replace(/[^\d.]/g, "");
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
  }
  return s;
};

const altIdentificacion = (idn) => {
  const s = String(idn || "").trim();
  if (!s) return "";
  if (isDigits(s) && s.length === 10) return s + "001";
  if (isDigits(s) && s.length === 13 && s.endsWith("001")) return s.slice(0, 10);
  return "";
};

const docPrefix = (tipoDoc) => {
  const t = String(tipoDoc || "").toUpperCase().trim();
  if (t === "FACTURA" || t === "01") return "FAC";
  if (t === "NOTA_CREDITO" || t === "04") return "NC";
  if (t === "NOTA_DEBITO" || t === "05") return "ND";
  if (t === "GUIA_REMISION" || t === "06") return "GR";
  if (t === "RETENCION" || t === "07") return "RET";
  return "";
};

const formatDocCell = (tipoDoc, numero) => {
  const n = String(numero || "").trim();
  const p = docPrefix(tipoDoc);
  if (!n) return "";
  return p ? `${p} ${n}` : n;
};

const onlyDate = (v) => {
  const s = String(v || "").trim();
  if (!s) return "";
  return s.split("T")[0].split(" ")[0];
};

const docsFromPayment = (p) => {
  const detalles = Array.isArray(p?.detalles) ? p.detalles : [];

  if (!detalles.length) {
    return String(p?.numero_documento || p?.documento || "").trim();
  }

  return detalles
    .map((d) => {
      const numero = String(d?.numero_documento || d?.documento || d?.doc_id || "").trim();
      const tipoDoc = String(d?.tipo_documento || "").trim();
      return formatDocCell(tipoDoc, numero);
    })
    .filter(Boolean)
    .join(", ");
};

const DocumentPaymentDetail = ({
  value = [],
  onChange,
  identificacion = "",
  personaId = "",
  tipo = "PAGO",
}) => {
  const { accessToken } = useContext(AppContext);

  const [rows, setRows] = useState(Array.isArray(value) ? value : []);
  const [openModal, setOpenModal] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [docs, setDocs] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);

  const [openHist, setOpenHist] = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);
  const [hist, setHist] = useState([]);
  const [histDetail, setHistDetail] = useState(null);

  const reqRef = useRef(0);
  const mountedRef = useRef(true);
  const abortRef = useRef(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        abortRef.current?.abort?.();
      } catch {}
    };
  }, []);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    setRows(Array.isArray(value) ? value : []);
  }, [value]);

  useEffect(() => {
    onChangeRef.current?.(rows);
  }, [rows]);

  useEffect(() => {
    reqRef.current += 1;
    try {
      abortRef.current?.abort?.();
    } catch {}
    setOpenModal(false);
    setDocs([]);
    setActiveRowId(null);
    setLoadingDocs(false);
    setOpenHist(false);
    setHist([]);
    setHistDetail(null);
    setLoadingHist(false);
  }, [tipo, identificacion, personaId]);

  const authFetch = async (url, options = {}) => {
    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    return fetch(url, {
      ...options,
      credentials: "omit",
      headers,
    });
  };

  const addRow = () => {
    setRows((p) => [
      ...p,
      {
        id: crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
        doc_id: "",
        documento: "",
        fecha_emision: "",
        tipo_documento: "",
        persona: "",
        valor: 0,
        total: 0,
        pagado: 0,
        saldo: 0,
        pago: "",
        origen: "",
      },
    ]);
  };

  const removeRow = (id) => {
    setRows((p) => p.filter((r) => r.id !== id));
  };

  const updatePagoInput = (id, v) => {
    const next = normalizeMoneyInput(v);
    setRows((p) =>
      p.map((r) => {
        if (r.id !== id) return r;
        return { ...r, pago: next };
      })
    );
  };

  const commitPagoInput = (id, v) => {
    setRows((p) =>
      p.map((r) => {
        if (r.id !== id) return r;
        const saldo = asMoney(r.saldo || 0);
        const raw = normalizeMoneyInput(v);
        if (raw === "") return { ...r, pago: "" };
        if (raw === ".") return { ...r, pago: "" };
        const num = asMoney(raw);
        const fixed = clamp(num, 0, saldo);
        return { ...r, pago: fixed.toFixed(2) };
      })
    );
  };

  const copySaldoToPago = (id) => {
    setRows((p) =>
      p.map((r) => {
        if (r.id !== id) return r;
        const saldo = asMoney(r.saldo || 0);
        return { ...r, pago: saldo.toFixed(2) };
      })
    );
  };

  const totalPago = useMemo(() => rows.reduce((acc, r) => acc + asMoney(r.pago || 0), 0), [rows]);

  const buildDocumentosUrl = (idn, tipoLocal, personaIdLocal) => {
    const qs = new URLSearchParams();
    qs.set("tipo", String(tipoLocal || "").toUpperCase());

    if (personaIdLocal) {
      qs.set("persona_id", personaIdLocal);
    }

    if (idn) {
      qs.set("identificacion", idn);
    }

    return `/cuentas/documentos-por-persona/?${qs.toString()}`;
  };

  const doFetch = async (idn, tipoLocal, personaIdLocal, signal) => {
    const url = buildDocumentosUrl(idn, tipoLocal, personaIdLocal);
    const r = await authFetch(url, { method: "GET", signal });
    const ct = (r.headers.get("content-type") || "").toLowerCase();

    if (!ct.includes("application/json")) throw new Error("NOT_JSON");

    const j = await r.json();

    if (r.status === 401 || r.status === 403) throw new Error(j?.detail || j?.error || "NO_AUTORIZADO");
    if (!r.ok) throw new Error(j?.error || "NO_SE_PUDO_CARGAR");

    const arr = Array.isArray(j?.results) ? j.results : Array.isArray(j) ? j : [];

    return arr.filter((x) => asMoney(x?.saldo || 0) > 0);
  };

  const fetchDocs = async () => {
    const idn = String(identificacion || "").trim();
    const pid = String(personaId || "").trim();
    const tipoLocal = String(tipo || "").toUpperCase();

    if ((!idn && !pid) || !accessToken) {
      if (!mountedRef.current) return;
      setDocs([]);
      return;
    }

    const myReq = ++reqRef.current;

    try {
      abortRef.current?.abort?.();
    } catch {}

    const controller = new AbortController();
    abortRef.current = controller;

    if (mountedRef.current) {
      setLoadingDocs(true);
      setDocs([]);
    }

    try {
      let arr = await doFetch(idn, tipoLocal, pid, controller.signal);

      if (arr.length === 0 && !pid && tipoLocal === "PAGO") {
        const alt = altIdentificacion(idn);
        if (alt) {
          const arr2 = await doFetch(alt, tipoLocal, "", controller.signal);
          if (arr2.length) arr = arr2;
        }
      }

      if (!mountedRef.current) return;
      if (reqRef.current !== myReq) return;

      setDocs(arr);
    } catch {
      if (!mountedRef.current) return;
      if (reqRef.current !== myReq) return;
      setDocs([]);
    } finally {
      if (!mountedRef.current) return;
      if (reqRef.current !== myReq) return;
      setLoadingDocs(false);
    }
  };

  const openPicker = (rowId) => {
    setActiveRowId(rowId);
    setOpenModal(true);
    fetchDocs();
  };

  const pickDoc = (d) => {
    if (!activeRowId) return;

    const docId = String(d?.id || d?.doc_id || "");
    const numero = String(d?.documento || d?.numero_documento || "");
    const fecha = String(d?.fecha_emision || "");
    const persona = String(d?.persona || d?.comprador_razon_social || d?.razon_social || "");
    const origen = String(d?.origen || "");
    const tipoDoc = String(d?.tipo_documento || "");
    const total = asMoney(d?.total ?? d?.valor_total ?? d?.valor ?? 0);
    const pagado = asMoney(d?.pagado ?? 0);
    const saldo = asMoney(d?.saldo ?? d?.saldo_calc ?? 0);

    if (saldo <= 0) return;

    setRows((prev) => {
      const exists = prev.some((x) => String(x.doc_id || "") !== "" && String(x.doc_id) === docId);

      return prev.map((r) => {
        if (r.id !== activeRowId) return r;
        if (exists && String(r.doc_id || "") !== docId) return r;

        const rawPago = normalizeMoneyInput(r.pago ?? "");
        const pagoActual = rawPago === "" || rawPago === "." ? 0 : asMoney(rawPago);
        const pagoClamped = clamp(pagoActual, 0, saldo);

        return {
          ...r,
          doc_id: docId,
          documento: numero,
          fecha_emision: fecha,
          tipo_documento: tipoDoc,
          persona,
          valor: total,
          total,
          pagado,
          saldo,
          origen,
          pago: pagoClamped > 0 ? pagoClamped.toFixed(2) : saldo.toFixed(2),
        };
      });
    });

    setOpenModal(false);
    setActiveRowId(null);
  };

  const buildHistorialUrl = () => {
    const qs = new URLSearchParams();
    qs.set("tipo", String(tipo || "").toUpperCase());

    if (String(personaId || "").trim()) {
      qs.set("persona_id", String(personaId || "").trim());
    }

    if (String(identificacion || "").trim()) {
      qs.set("identificacion", String(identificacion || "").trim());
    }

    return `/cuentas/pagos/historial/?${qs.toString()}`;
  };

  const fetchHistorial = async () => {
    const idn = String(identificacion || "").trim();
    const pid = String(personaId || "").trim();

    if ((!idn && !pid) || !accessToken) return;

    setOpenHist(true);
    setHist([]);
    setHistDetail(null);
    setLoadingHist(true);

    try {
      const url = buildHistorialUrl();
      const r = await authFetch(url, { method: "GET" });
      const ct = (r.headers.get("content-type") || "").toLowerCase();

      if (!ct.includes("application/json")) throw new Error("NOT_JSON");

      const j = await r.json();

      if (r.status === 401 || r.status === 403) throw new Error(j?.detail || j?.error || "NO_AUTORIZADO");
      if (!r.ok) throw new Error(j?.error || "NO_SE_PUDO_CARGAR");

      setHist(Array.isArray(j?.results) ? j.results : []);
    } catch {
      setHist([]);
    } finally {
      setLoadingHist(false);
    }
  };

  const canSearch = Boolean((String(identificacion || "").trim() || String(personaId || "").trim()) && accessToken);

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700 }}>Documentos pendientes</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 12 }}>
            Solo se muestran documentos con saldo pendiente.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<HistoryIcon />}
          onClick={fetchHistorial}
          disabled={!canSearch}
          sx={{ textTransform: "none", borderColor: "#1565C0", color: "#1565C0" }}
        >
          Ver historial
        </Button>
      </Box>

      <Table size="small">
        <TableHead>
          <TableRow sx={headerRowSx}>
            <TableCell sx={headerCellSx} />
            <TableCell sx={headerCellSx}>Documento</TableCell>
            <TableCell sx={headerCellSx}>Fecha Emisión</TableCell>
            <TableCell sx={headerCellSx}>Tipo Documento</TableCell>
            <TableCell sx={headerCellSx} align="right">Total</TableCell>
            <TableCell sx={headerCellSx} align="right">Pagado</TableCell>
            <TableCell sx={headerCellSx} align="right">Saldo</TableCell>
            <TableCell sx={headerCellSx} align="right">Valor a registrar</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((row) => {
            const saldo = asMoney(row.saldo || 0);
            const pagoInput = String(row.pago ?? "");

            return (
              <TableRow key={row.id}>
                <TableCell>
                  <IconButton onClick={() => removeRow(row.id)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </TableCell>

                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    value={formatDocCell(row.tipo_documento, row.documento) || ""}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => openPicker(row.id)} disabled={!accessToken}>
                            <SearchIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>

                <TableCell>{row.fecha_emision || ""}</TableCell>
                <TableCell>{row.tipo_documento || ""}</TableCell>
                <TableCell align="right">${asMoney(row.total ?? row.valor ?? 0).toFixed(2)}</TableCell>
                <TableCell align="right">${asMoney(row.pagado || 0).toFixed(2)}</TableCell>
                <TableCell align="right">${saldo.toFixed(2)}</TableCell>

                <TableCell align="right">
                  <TextField
                    size="small"
                    type="text"
                    value={pagoInput}
                    onChange={(e) => updatePagoInput(row.id, e.target.value)}
                    onBlur={(e) => commitPagoInput(row.id, e.target.value)}
                    inputProps={{ inputMode: "decimal" }}
                    sx={{ width: 180 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => copySaldoToPago(row.id)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </TableCell>
              </TableRow>
            );
          })}

          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} align="center">
                No hay documentos agregados al pago.
              </TableCell>
            </TableRow>
          )}

          <TableRow>
            <TableCell colSpan={7} align="right">
              <Typography>Total:</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography>${totalPago.toFixed(2)}</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
        <Button variant="contained" onClick={addRow} sx={{ textTransform: "none" }} startIcon={<AddIcon />}>
          Agregar Detalle
        </Button>
      </Box>

      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 800 }}>Seleccionar Documento Pendiente</DialogTitle>

        <DialogContent>
          {!String(identificacion || "").trim() && !String(personaId || "").trim() ? (
            <Typography sx={{ py: 2 }} color="error">
              Seleccione una persona válida.
            </Typography>
          ) : !accessToken ? (
            <Typography sx={{ py: 2 }} color="error">
              Sesión expirada. Inicia sesión nuevamente.
            </Typography>
          ) : loadingDocs ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Buscando documentos pendientes…</Typography>
            </Box>
          ) : (
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow sx={headerRowSx}>
                  <TableCell sx={headerCellSx}>Fecha</TableCell>
                  <TableCell sx={headerCellSx}>Documento</TableCell>
                  <TableCell sx={headerCellSx}>Persona</TableCell>
                  <TableCell sx={headerCellSx} align="right">Total</TableCell>
                  <TableCell sx={headerCellSx} align="right">Pagado</TableCell>
                  <TableCell sx={headerCellSx} align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {docs.map((d) => {
                  const docId = String(d?.id || d?.doc_id || "");
                  const fecha = onlyDate(d?.fecha_emision || "");
                  const numero = String(d?.documento || d?.numero_documento || "");
                  const tipoDoc = String(d?.tipo_documento || "");
                  const persona = String(d?.persona || d?.comprador_razon_social || d?.razon_social || "");
                  const docCell = formatDocCell(tipoDoc, numero);

                  return (
                    <TableRow
                      key={docId || `${fecha}-${docCell}-${persona}`}
                      hover
                      onClick={() => pickDoc(d)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{fecha}</TableCell>
                      <TableCell>{docCell}</TableCell>
                      <TableCell>{persona}</TableCell>
                      <TableCell align="right">${asMoney(d?.total || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">${asMoney(d?.pagado || 0).toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={`$${asMoney(d?.saldo || 0).toFixed(2)}`}
                          color="warning"
                          sx={{ fontWeight: 800 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {docs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No se encontraron documentos con saldo pendiente para esta persona.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openHist} onClose={() => setOpenHist(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ fontWeight: 800 }}>Historial de {String(tipo || "").toUpperCase()}</DialogTitle>

        <DialogContent>
          {!String(identificacion || "").trim() && !String(personaId || "").trim() ? (
            <Typography sx={{ py: 2 }} color="error">
              Seleccione una persona válida.
            </Typography>
          ) : !accessToken ? (
            <Typography sx={{ py: 2 }} color="error">
              Sesión expirada. Inicia sesión nuevamente.
            </Typography>
          ) : loadingHist ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }}>Cargando historial…</Typography>
            </Box>
          ) : (
            <>
              <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                  <TableRow sx={headerRowSx}>
                    <TableCell sx={headerCellSx}>Fecha</TableCell>
                    <TableCell sx={headerCellSx}>Forma</TableCell>
                    <TableCell sx={headerCellSx}>Cuenta</TableCell>
                    <TableCell sx={headerCellSx}>Banco</TableCell>
                    <TableCell sx={headerCellSx}>Comprobante</TableCell>
                    <TableCell sx={headerCellSx}>Documento</TableCell>
                    <TableCell sx={headerCellSx} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {hist.map((p) => (
                    <TableRow
                      key={String(p?.id)}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => setHistDetail(p)}
                    >
                      <TableCell>{onlyDate(p?.fecha)}</TableCell>
                      <TableCell>{String(p?.forma || "")}</TableCell>
                      <TableCell>{`${String(p?.cuenta_codigo || "")} ${String(p?.cuenta || "")}`.trim()}</TableCell>
                      <TableCell>{String(p?.banco || "")}</TableCell>
                      <TableCell>{String(p?.comprobante || "")}</TableCell>
                      <TableCell>{docsFromPayment(p)}</TableCell>
                      <TableCell align="right">${asMoney(p?.total || 0).toFixed(2)}</TableCell>
                    </TableRow>
                  ))}

                  {hist.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No hay pagos/cobros registrados para esta persona.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {histDetail && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography sx={{ fontWeight: 800, color: "#1565C0", mb: 1 }}>
                    Detalle del movimiento #{String(histDetail?.id)}
                  </Typography>

                  <Table size="small">
                    <TableHead>
                      <TableRow sx={headerRowSx}>
                        <TableCell sx={headerCellSx}>Origen</TableCell>
                        <TableCell sx={headerCellSx}>Documento</TableCell>
                        <TableCell sx={headerCellSx}>Fecha</TableCell>
                        <TableCell sx={headerCellSx} align="right">Valor aplicado</TableCell>
                        <TableCell sx={headerCellSx} align="right">Saldo documento</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {(Array.isArray(histDetail?.detalles) ? histDetail.detalles : []).map((d, idx) => {
                        const numero = String(d?.numero_documento || d?.documento || d?.doc_id || "");
                        const tipoDoc = String(d?.tipo_documento || "");
                        const label = formatDocCell(tipoDoc, numero);

                        return (
                          <TableRow key={`${d?.origen}-${d?.doc_id}-${idx}`}>
                            <TableCell>{String(d?.origen || "")}</TableCell>
                            <TableCell>{label}</TableCell>
                            <TableCell>{onlyDate(d?.fecha_emision)}</TableCell>
                            <TableCell align="right">${asMoney(d?.valor || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {d?.saldo !== undefined && d?.saldo !== null && d?.saldo !== ""
                                ? `$${asMoney(d?.saldo || 0).toFixed(2)}`
                                : "-"}
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {!Array.isArray(histDetail?.detalles) || histDetail.detalles.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            Sin detalles.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DocumentPaymentDetail;